// src/components/UserMenu.tsx
//
// Top-right user menu for the app shell: click the user's name to open a
// dropdown with the tenant switcher (multi-tenant admins), text-size control,
// and sign out. Replaces the old inline name + logout button in page headers.
//
// Switching tenant calls setActiveTenant (which re-points every API singleton
// and the x-tenant-slug header); AppShell is keyed by the active slug so the
// content remounts cleanly on switch.

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut, Check, Building2 } from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import DisplaySettings from './DisplaySettings';

export default function UserMenu() {
  const { user, tenants, activeTenant, setActiveTenant, logout } = useAuthContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() || user.name : 'User';
  const multiTenant = tenants.length > 1;

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2">
        <DisplaySettings />
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 hover:border-slate-600/70 transition-colors"
          title="Account & tenant"
        >
          <div className="w-7 h-7 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-semibold shrink-0">
            {displayName.slice(0, 1).toUpperCase()}
          </div>
          <div className="text-left hidden sm:block max-w-[10rem]">
            <div className="text-sm font-medium text-white leading-tight truncate">{displayName}</div>
            {activeTenant && (
              <div className="text-[11px] text-slate-400 leading-tight truncate">{activeTenant.name}</div>
            )}
          </div>
          <ChevronDown size={16} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-700/60 bg-slate-900 shadow-2xl shadow-black/40 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700/50">
            <div className="text-sm font-semibold text-white truncate">{displayName}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email || ''}</div>
          </div>

          {multiTenant && (
            <div className="py-1 max-h-72 overflow-auto">
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Building2 size={11} /> Switch tenant
              </div>
              {tenants.map((t) => {
                const active = t.slug === activeTenant?.slug;
                return (
                  <button
                    key={t.slug}
                    onClick={() => { if (!active) setActiveTenant(t); setOpen(false); }}
                    className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors ${
                      active ? 'text-white bg-slate-800/60' : 'text-slate-300 hover:bg-slate-800/40'
                    }`}
                  >
                    <span className="truncate">{t.name}</span>
                    {active && <Check size={15} className="text-blue-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t border-slate-700/50 py-1">
            <button
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:text-red-400 hover:bg-slate-800/40 transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
